/**
 * Thumbs-up / thumbs-down state for a clinical document: approve fires an
 * analytics event immediately; reject opens a comment modal and fires on submit.
 *
 * @module documentFeedback/useDocumentFeedback
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useDataAccumulatorForSingleOrder } from "../../context/dataAccumulatorForSingleOrder";
import { EventsName } from "../../enums/eventsName";
import { RootState } from "../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { apiWorkerManager } from "../../workerManagers/workerManager";
import { PdfToolbarFeedback } from "../oasisPdfToolbar/OasisPdfToolbar";
import {
  DOCUMENT_FEEDBACK_APPROVED,
  DOCUMENT_FEEDBACK_REJECTED,
  DOCUMENT_FEEDBACK_SUCCESS_TOAST,
} from "./constants";

/** Inputs for {@link useDocumentFeedback}. */
export interface UseDocumentFeedbackParams {
  /** Screen that hosts the document viewer (included on the analytics event). */
  screenName: string;
}

/** Return value of {@link useDocumentFeedback}. */
export interface UseDocumentFeedbackReturn {
  /** Currently selected thumb, or null when unset. */
  feedback: PdfToolbarFeedback;
  /** Handles a toolbar thumb click (approve, reject, or clear). */
  handleFeedback: (next: PdfToolbarFeedback) => void;
  /** Whether the thumbs-down comment dialog is open. */
  isFeedbackModalOpen: boolean;
  /** Persists thumbs-down feedback and closes the dialog. */
  submitNegativeFeedback: (comment: string) => void;
  /** Closes the thumbs-down dialog without changing selection. */
  closeFeedbackModal: () => void;
}

/**
 * Owns document-rating selection and emits
 * {@link EventsName.CLINICAL_DOCUMENT_FEEDBACK_SUBMITTED} through the analytics
 * worker (BigQuery + Amplitude).
 *
 * @param params - Screen name used as analytics context
 * @returns Selection state, toolbar handler, and modal controls
 */
export const useDocumentFeedback = ({
  screenName,
}: UseDocumentFeedbackParams): UseDocumentFeedbackReturn => {
  const [feedback, setFeedback] = useState<PdfToolbarFeedback>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const { generalOrderDetails, coverageDetails } =
    useDataAccumulatorForSingleOrder();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgId = getOrgIdForFetchExternalWorklist();
  const order = generalOrderDetails?.order;
  const payerName =
    coverageDetails?.coverages?.find(
      (coverage) => coverage.coverage_type === "primary",
    )?.payer_name ?? "";

  /**
   * Enqueues the document-feedback analytics event.
   *
   * @param rating - Thumbs-up or thumbs-down value
   * @param comment - Free-text reason; empty for thumbs-up
   */
  const emitFeedbackEvent = (rating: string, comment: string): void => {
    void apiWorkerManager.enqueueAndProcess({
      patient_id:
        order?.demographics?.patient_id ??
        order?.demographics?.patient_mrn ??
        "",
      order_id: order?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.CLINICAL_DOCUMENT_FEEDBACK_SUBMITTED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: screenName,
        feedback: rating,
        comment,
        payer_name: payerName,
        regimen_name: order?.payload?.regimen_name ?? "",
      },
    });
  };

  /**
   * Applies a toolbar selection: thumbs-up logs immediately, thumbs-down
   * opens the comment modal, and a cleared selection only updates local state.
   *
   * @param next - Next toolbar value, including null when toggling off
   */
  const handleFeedback = (next: PdfToolbarFeedback): void => {
    if (next === "down") {
      setIsFeedbackModalOpen(true);
      return;
    }
    setFeedback(next);
    if (next === "up") {
      emitFeedbackEvent(DOCUMENT_FEEDBACK_APPROVED, "");
    }
  };

  /**
   * Records thumbs-down feedback with the reviewer's comment.
   *
   * @param comment - Trimmed free-text feedback from the modal
   */
  const submitNegativeFeedback = (comment: string): void => {
    setFeedback("down");
    setIsFeedbackModalOpen(false);
    emitFeedbackEvent(DOCUMENT_FEEDBACK_REJECTED, comment);
    toast.success(DOCUMENT_FEEDBACK_SUCCESS_TOAST);
  };

  /** Dismisses the thumbs-down dialog without changing the selected thumb. */
  const closeFeedbackModal = (): void => {
    setIsFeedbackModalOpen(false);
  };

  return {
    feedback,
    handleFeedback,
    isFeedbackModalOpen,
    submitNegativeFeedback,
    closeFeedbackModal,
  };
};
